import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { EquipmentServiceRate, EquipmentType } from '@jobos/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useAddEquipmentServiceRate,
  useAddEquipmentType,
  useDeleteEquipmentServiceRate,
  useDeleteEquipmentType,
  useEquipmentCatalog,
  useUpdateEquipmentServiceRate,
  useUpdateEquipmentType,
} from '@/hooks/useEquipmentCatalog';

function generatedIds(prefix: string, qty: number): string {
  const trimmed = prefix.trim();
  if (!trimmed || qty <= 0) return '—';
  const ids: string[] = [];
  for (let i = 1; i <= Math.min(qty, 12); i++) ids.push(`${trimmed}${i}`);
  return ids.join(', ') + (qty > 12 ? `, … (${qty})` : '');
}

interface TypeRowProps {
  item: EquipmentType;
}

function TypeRow({ item }: TypeRowProps) {
  const updateMutation = useUpdateEquipmentType();
  const deleteMutation = useDeleteEquipmentType();

  const [label, setLabel] = useState(item.label);
  const [setupFee, setSetupFee] = useState(Number(item.setupFee).toFixed(2));
  const [dailyRate, setDailyRate] = useState(Number(item.dailyRate).toFixed(2));
  const [idPrefix, setIdPrefix] = useState(item.idPrefix);
  const [minDays, setMinDays] = useState(String(item.minDays));
  const [qty, setQty] = useState(String(item.qty));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setLabel(item.label);
    setSetupFee(Number(item.setupFee).toFixed(2));
    setDailyRate(Number(item.dailyRate).toFixed(2));
    setIdPrefix(item.idPrefix);
    setMinDays(String(item.minDays));
    setQty(String(item.qty));
  }, [item.id, item.label, item.setupFee, item.dailyRate, item.idPrefix, item.minDays, item.qty]);

  const commit = (patch: Partial<EquipmentType>) => {
    updateMutation.mutate({ id: item.id, ...patch });
  };

  const onLabelBlur = () => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === item.label) {
      setLabel(item.label);
      return;
    }
    commit({ label: trimmed });
  };

  const onNumberBlur = (
    raw: string,
    current: number,
    field: 'setupFee' | 'dailyRate' | 'minDays' | 'qty',
    integer = false,
  ) => {
    const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
    if (isNaN(parsed) || parsed < 0) {
      // revert
      if (field === 'setupFee') setSetupFee(Number(item.setupFee).toFixed(2));
      if (field === 'dailyRate') setDailyRate(Number(item.dailyRate).toFixed(2));
      if (field === 'minDays') setMinDays(String(item.minDays));
      if (field === 'qty') setQty(String(item.qty));
      return;
    }
    if (parsed === current) return;
    commit({ [field]: parsed } as Partial<EquipmentType>);
  };

  const onPrefixBlur = () => {
    const trimmed = idPrefix.trim();
    if (!trimmed || trimmed === item.idPrefix) {
      setIdPrefix(item.idPrefix);
      return;
    }
    commit({ idPrefix: trimmed });
  };

  const handleDelete = () => {
    deleteMutation.mutate(item.id, { onSettled: () => setConfirmingDelete(false) });
  };

  const previewQty = parseInt(qty, 10);
  const preview = generatedIds(idPrefix, isNaN(previewQty) ? item.qty : previewQty);

  return (
    <tr className="border-b last:border-b-0 hover:bg-gray-50 group">
      <td className="px-3 py-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={onLabelBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setLabel(item.label);
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          className="h-7 text-sm"
        />
      </td>
      <td className="px-3 py-2 w-[110px]">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={setupFee}
            onChange={(e) => setSetupFee(e.target.value)}
            onBlur={() => onNumberBlur(setupFee, Number(item.setupFee), 'setupFee')}
            onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
            className="w-20 h-7 text-sm tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2 w-[110px]">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={dailyRate}
            onChange={(e) => setDailyRate(e.target.value)}
            onBlur={() => onNumberBlur(dailyRate, Number(item.dailyRate), 'dailyRate')}
            onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
            className="w-20 h-7 text-sm tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2 w-[80px]">
        <Input
          value={idPrefix}
          maxLength={8}
          onChange={(e) => setIdPrefix(e.target.value)}
          onBlur={onPrefixBlur}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          className="w-16 h-7 text-sm"
        />
      </td>
      <td className="px-3 py-2 w-[80px]">
        <Input
          type="number"
          min="0"
          step="1"
          value={minDays}
          onChange={(e) => setMinDays(e.target.value)}
          onBlur={() => onNumberBlur(minDays, item.minDays, 'minDays', true)}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          className="w-16 h-7 text-sm tabular-nums"
        />
      </td>
      <td className="px-3 py-2 w-[80px]">
        <Input
          type="number"
          min="0"
          step="1"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => onNumberBlur(qty, item.qty, 'qty', true)}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          className="w-16 h-7 text-sm tabular-nums"
        />
      </td>
      <td className="px-3 py-2 text-xs font-mono text-gray-500">{preview}</td>
      <td className="px-3 py-2 w-[60px] text-right">
        {confirmingDelete ? (
          <div className="inline-flex items-center gap-1">
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="h-6 text-xs px-2"
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
              className="h-6 text-xs px-2"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmingDelete(true)}
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label={`Delete ${item.label}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-gray-500" />
          </Button>
        )}
      </td>
    </tr>
  );
}

function AddTypeRow() {
  const addMutation = useAddEquipmentType();
  const [label, setLabel] = useState('');
  const [setupFee, setSetupFee] = useState('');
  const [dailyRate, setDailyRate] = useState('');
  const [idPrefix, setIdPrefix] = useState('');
  const [minDays, setMinDays] = useState('0');
  const [qty, setQty] = useState('1');

  const reset = () => {
    setLabel('');
    setSetupFee('');
    setDailyRate('');
    setIdPrefix('');
    setMinDays('0');
    setQty('1');
  };

  const canAdd =
    label.trim().length > 0 &&
    idPrefix.trim().length > 0 &&
    !isNaN(parseFloat(setupFee || '0')) &&
    !isNaN(parseFloat(dailyRate || '0')) &&
    !isNaN(parseInt(qty, 10));

  const handleAdd = () => {
    if (!canAdd) return;
    addMutation.mutate(
      {
        label: label.trim(),
        setupFee: parseFloat(setupFee || '0'),
        dailyRate: parseFloat(dailyRate || '0'),
        idPrefix: idPrefix.trim(),
        minDays: parseInt(minDays, 10) || 0,
        qty: parseInt(qty, 10) || 0,
      },
      { onSuccess: () => reset() },
    );
  };

  return (
    <tr className="bg-gray-50/60">
      <td className="px-3 py-2">
        <Input
          placeholder="New type label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="h-7 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={setupFee}
            onChange={(e) => setSetupFee(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="w-20 h-7 text-sm tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={dailyRate}
            onChange={(e) => setDailyRate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="w-20 h-7 text-sm tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2">
        <Input
          placeholder="X"
          maxLength={8}
          value={idPrefix}
          onChange={(e) => setIdPrefix(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="w-16 h-7 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          min="0"
          step="1"
          value={minDays}
          onChange={(e) => setMinDays(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="w-16 h-7 text-sm tabular-nums"
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          min="0"
          step="1"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="w-16 h-7 text-sm tabular-nums"
        />
      </td>
      <td className="px-3 py-2 text-xs font-mono text-gray-400">
        {generatedIds(idPrefix || 'X', parseInt(qty, 10) || 0)}
      </td>
      <td className="px-3 py-2 w-[60px] text-right">
        <Button
          onClick={handleAdd}
          disabled={!canAdd || addMutation.isPending}
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </td>
    </tr>
  );
}

interface ServiceRateRowProps {
  item: EquipmentServiceRate;
}

function ServiceRateRow({ item }: ServiceRateRowProps) {
  const updateMutation = useUpdateEquipmentServiceRate();
  const deleteMutation = useDeleteEquipmentServiceRate();
  const [label, setLabel] = useState(item.label);
  const [rate, setRate] = useState(Number(item.rate).toFixed(2));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setLabel(item.label);
    setRate(Number(item.rate).toFixed(2));
  }, [item.id, item.label, item.rate]);

  const onLabelBlur = () => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === item.label) {
      setLabel(item.label);
      return;
    }
    updateMutation.mutate({ id: item.id, label: trimmed });
  };

  const onRateBlur = () => {
    const parsed = parseFloat(rate);
    if (isNaN(parsed) || parsed < 0) {
      setRate(Number(item.rate).toFixed(2));
      return;
    }
    if (parsed === Number(item.rate)) return;
    updateMutation.mutate({ id: item.id, rate: parsed });
  };

  return (
    <tr className="border-b last:border-b-0 hover:bg-gray-50 group">
      <td className="px-3 py-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={onLabelBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setLabel(item.label);
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          className="h-7 text-sm"
        />
      </td>
      <td className="px-3 py-2 w-[140px]">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onBlur={onRateBlur}
            onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
            className="w-24 h-7 text-sm tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2 w-[60px] text-right">
        {confirmingDelete ? (
          <div className="inline-flex items-center gap-1">
            <Button
              size="sm"
              variant="destructive"
              onClick={() => deleteMutation.mutate(item.id, { onSettled: () => setConfirmingDelete(false) })}
              disabled={deleteMutation.isPending}
              className="h-6 text-xs px-2"
            >
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
              className="h-6 text-xs px-2"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmingDelete(true)}
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label={`Delete ${item.label}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-gray-500" />
          </Button>
        )}
      </td>
    </tr>
  );
}

function AddServiceRateRow() {
  const addMutation = useAddEquipmentServiceRate();
  const [label, setLabel] = useState('');
  const [rate, setRate] = useState('');

  const canAdd = label.trim().length > 0 && !isNaN(parseFloat(rate));

  const handleAdd = () => {
    if (!canAdd) return;
    // multi-date is retired; new rates are always single-date.
    addMutation.mutate(
      { label: label.trim(), rate: parseFloat(rate), multiDate: false },
      {
        onSuccess: () => {
          setLabel('');
          setRate('');
        },
      },
    );
  };

  return (
    <tr className="bg-gray-50/60">
      <td className="px-3 py-2">
        <Input
          placeholder="New service rate label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="h-7 text-sm"
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="w-24 h-7 text-sm tabular-nums"
          />
        </div>
      </td>
      <td className="px-3 py-2 w-[60px] text-right">
        <Button
          onClick={handleAdd}
          disabled={!canAdd || addMutation.isPending}
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </td>
    </tr>
  );
}

export default function EquipmentSettings() {
  const { data, isLoading } = useEquipmentCatalog();
  const types = data?.types ?? [];
  const serviceRates = data?.serviceRates ?? [];

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Equipment</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Equipment</h2>
        <p className="text-sm text-gray-500 mb-4">
          One row per equipment type. <code className="text-xs">Qty</code> generates asset IDs (e.g.{' '}
          <code className="text-xs">D1…D4</code>) on demand. Edits autosave on blur.
        </p>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Type</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Setup fee</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Daily rate</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">ID prefix</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Min days</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Qty</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Generated IDs</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {types.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                    No equipment types yet. Add one below.
                  </td>
                </tr>
              ) : (
                types.map((t) => <TypeRow key={t.id} item={t} />)
              )}
              <AddTypeRow />
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Service rates</h3>
        <p className="text-sm text-gray-500 mb-4">Charges that aren't asset-tagged.</p>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Label</th>
                <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Rate</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {serviceRates.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                    No service rates yet. Add one below.
                  </td>
                </tr>
              ) : (
                serviceRates.map((s) => <ServiceRateRow key={s.id} item={s} />)
              )}
              <AddServiceRateRow />
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
